import { PayoutOrderService } from '../../../PayoutDefinitionSetup.types';

export const SERVICE_VALIDATION_ERROR_TYPE = {
  empty: 'empty',
  gap: 'gap',
  duplicate: 'duplicate',
  manual: 'manual',
} as const;

export type ServiceValidationErrorType =
  (typeof SERVICE_VALIDATION_ERROR_TYPE)[keyof typeof SERVICE_VALIDATION_ERROR_TYPE];

export interface ValidationError {
  key: string;
  type: ServiceValidationErrorType;
  message: string;
}

export const FIELD_VALIDATION_MESSAGE = 'Add plan templates to define the payment order';
export const DUPLICATE_ERROR_MESSAGE = 'This plan template is already selected';
export const GLOBAL_VALIDATION_MESSAGE = 'Please correct the highlighted errors to save payout definition';

/**
 * Field key used in formState.errors for per-service-per-column validation.
 */
export const getServiceColumnErrorKey = (rowId: string, columnIndex: number) =>
  `service_${rowId}_col_${columnIndex}`;

/** True if a column holds a plan */
const isColumnFilled = (service: PayoutOrderService, col: number) =>
  service.plans.some((p) => p.order === col);

export const collectValidationErrors = (
  allServices: PayoutOrderService[],
  columnsCount: number
): ValidationError[] => {
  /**
   * Plans must be filled contiguously starting from Pay #1:
   * - Pay #1 can't be empty (at least one plan must be set).
   * - No gaps allowed: an empty column can't be followed by a filled one.
   * - Trailing empty columns are allowed (they mean "no more accounts to pay").
   */
  const orderErrors = allServices.flatMap((service) => {
    const columns = Array.from({ length: columnsCount }, (_, i) => i + 1);
    const filled = columns.map((col) => isColumnFilled(service, col));

    const lastFilledIndex = filled.lastIndexOf(true);

    // Nothing filled at all: Pay #1 is required.
    if (lastFilledIndex === -1) {
      return [
        {
          key: getServiceColumnErrorKey(service.rowId, 1),
          type: SERVICE_VALIDATION_ERROR_TYPE.empty,
          message: FIELD_VALIDATION_MESSAGE,
        },
      ];
    }

    return columns
      .slice(0, lastFilledIndex + 1)
      .map((col, index) => ({ col, index }))
      .filter(({ index }) => !filled[index])
      .map(({ col, index }) => ({
        key: getServiceColumnErrorKey(service.rowId, col),
        type: index === 0 ? SERVICE_VALIDATION_ERROR_TYPE.empty : SERVICE_VALIDATION_ERROR_TYPE.gap,
        message: FIELD_VALIDATION_MESSAGE,
      }));
  });

  const duplicateErrors = allServices.flatMap((service) => {
    const seen = new Set<string>();
    const duplicateIds = new Set<string>();

    for (const plan of service.plans) {
      if (seen.has(plan.id)) duplicateIds.add(plan.id);
      seen.add(plan.id);
    }

    return service.plans
      .filter((plan) => duplicateIds.has(plan.id))
      .map((plan) => ({
        key: getServiceColumnErrorKey(service.rowId, plan.order),
        type: SERVICE_VALIDATION_ERROR_TYPE.duplicate,
        message: DUPLICATE_ERROR_MESSAGE,
      }));
  });

  return [...orderErrors, ...duplicateErrors];
};
